/*
 * The engines a loader may show, as single-colour glyphs from Simple Icons
 * (CC0, simple-icons 16.33.0; OpenAI's from 15.22.0), each in its own brand colour. Gemini is the
 * exception: Simple Icons' one-colour `googlegemini` read as a fake, so it is
 * Google's own four-colour icon, the favicon gemini.google.com serves
 * (`gstatic.com/lamda/images/gemini_sparkle_4g_512_…png`, read 2026-10-08),
 * kept at 128px. Google AI Mode has no Simple Icons mark: its glyph is the icon
 * on Google's own "AI Mode" button (google.com, read 2026-10-08 and again
 * 2026-11-01: the same shape), scaled from Google's 960 grid to 24, in Google blue. Microsoft Copilot has no Simple Icons mark (GitHub
 * Copilot's is another product), and its mark is many colours: it is
 * Microsoft's own icon, the favicon copilot.microsoft.com serves
 * (`/static/cmc/favicon.svg`, read 2026-10-09), kept as that SVG.
 *
 * Glyphs rather than the logo.dev images `BrandMark` uses: a loader is on screen
 * before anything has been fetched, so it cannot wait on a third party to draw.
 *
 * Only the landing page draws them today, and imports this file directly; it is
 * kept out of the loader index so the app's first load does not carry it.
 *
 * Which of them a page may name is not this file's to say. In the app it is
 * the server's list of the engines it asks (`/api/plans`, from
 * `CAPTURE_ENGINES`); the public landing page names a fixed five, by
 * Abhishek's call (`apps/landing/src/loaders.ts`, `PAGE_ENGINES`).
 */

import copilotMark from "./copilot-mark.svg";
import geminiMark from "./gemini-mark.png";

/**
 * How an engine is drawn: a single-colour path, or, where the brand's mark is
 * itself many colours and a one-colour copy would read as fake, the brand's
 * own image.
 */
export type EngineGlyph = {
  readonly id:
    | "chatgpt"
    | "google_ai_overview"
    | "perplexity"
    | "gemini"
    | "google_ai_mode"
    | "copilot";
  readonly label: string;
} & (
  | {
      /** CSS colour for the glyph; `currentColor` follows the text (ChatGPT is ink). */
      readonly color: string;
      readonly d: string;
    }
  | {
      /** The brand's own image, square: a PNG of at least 128px, or its SVG. */
      readonly src: string;
    }
);

/**
 * Their own exports so the app can draw Gemini, Perplexity, AI Mode and Copilot
 * without carrying every glyph. logo.dev returns Google's "G" for gemini.google.com,
 * and AI Mode beside the AI Overview would be a second "G", so an engine row
 * draws these instead (`EngineLogo`).
 */
export const GEMINI_GLYPH: EngineGlyph = {
  id: "gemini",
  label: "Gemini",
  src: geminiMark,
};

export const COPILOT_GLYPH: EngineGlyph = {
  id: "copilot",
  label: "Copilot",
  src: copilotMark,
};

export const PERPLEXITY_GLYPH: EngineGlyph = {
  id: "perplexity",
  label: "Perplexity",
  color: "#1fb8cd",
  d: "M22.3977 7.0896h-2.3106V.0676l-7.5094 6.3542V.1577h-1.1554v6.1966L4.4904 0v7.0896H1.6023v10.3976h2.8882V24l6.932-6.3591v6.2005h1.1554v-6.0469l6.9318 6.1807v-6.4879h2.8882V7.0896zm-3.4657-4.531v4.531h-5.355l5.355-4.531zm-13.2862.0676 4.8691 4.4634H5.6458V2.6262zM2.7576 16.332V8.245h7.8476l-6.1149 6.1147v1.9723H2.7576zm2.8882 5.0404v-3.8852h.0001v-2.6488l5.7763-5.7764v7.0111l-5.7764 5.2993zm12.7086.0248-5.7766-5.1509V9.0618l5.7766 5.7766v6.5588zm2.8882-5.0652h-1.733v-1.9723L13.3948 8.245h7.8478v8.087z",
};

export const AI_MODE_GLYPH: EngineGlyph = {
  id: "google_ai_mode",
  label: "Google AI Mode",
  color: "#4285f4",
  d: "M18.5,10q-0.15,0 -0.2-0.15q-0.4-1.525 -1.513-2.638T14.15,5.7q-0.15-0.05 -0.15-0.2q0-0.175 0.15-0.2q1.525-0.4 2.638-1.513T18.3,1.15q0.05-0.15 0.2-0.15q0.175,0 0.2,0.15q0.425,1.525 1.525,2.638T22.85,5.3q0.15,0.025 0.15,0.2q0,0.15 -0.15,0.2q-1.525,0.4 -2.638,1.513T18.7,9.85q-0.025,0.15 -0.2,0.15Zm1.1,11L13.35,14.75q-0.85,0.625 -1.825,0.938T9.5,16q-2.725,0 -4.612-1.888T3,9.5t1.888-4.612T9.5,3q0.725,0 1.425,0.163T12.275,3.625l-1,1.75q-0.425-0.2 -0.875-0.288T9.5,5q-1.875,0 -3.188,1.312T5,9.5t1.312,3.188T9.5,14q1.725,0 3-1.137T13.975,10h2q-0.075,0.9 -0.375,1.763T14.75,13.35L21,19.6l-1.4,1.4Z",
};

/**
 * OpenAI's mark. Simple Icons dropped it in 16.0.0, so this path is from
 * 15.22.0, the last release that carried it (CC0). The path is exported for
 * the AI apps page, which draws ChatGPT's tile with it.
 */
export const OPENAI_MARK =
  "M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z";

export const CHATGPT_GLYPH: EngineGlyph = {
  id: "chatgpt",
  label: "ChatGPT",
  color: "currentColor",
  d: OPENAI_MARK,
};

export const ENGINE_GLYPHS: readonly EngineGlyph[] = [
  CHATGPT_GLYPH,
  {
    id: "google_ai_overview",
    label: "Google AI Overview",
    color: "#4285f4",
    d: "M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z",
  },
  AI_MODE_GLYPH,
  GEMINI_GLYPH,
  PERPLEXITY_GLYPH,
  COPILOT_GLYPH,
];
